'use client';

import { useMemo } from 'react';

import { useGetAudience } from '../use-get-audience';
import { useGetProducts } from '../use-get-products';
import { getTotalBoxCountForProduct } from './utils';

/**
 * ### useGetBoxCountsPerProduct
 *
 * @description
 * Hook that calculates the total box count for each product based on test groups and placements.
 * Returns a record mapping product IDs to their total box counts.
 *
 * @returns Object containing box counts data, loading state, and error state
 */
const useGetBoxCountsPerProduct = () => {
  const {
    data: audience,
    isLoading: isLoadingAudience,
    isError: isErrorAudience,
  } = useGetAudience();
  const {
    data: products,
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
  } = useGetProducts();

  const data = useMemo<Record<string, number>>(() => {
    if (!products) {
      return {};
    }

    return products.reduce<Record<string, number>>((acc, product) => {
      if (product.id === undefined) {
        return acc;
      }

      const totalBoxCount = getTotalBoxCountForProduct(
        audience?.legacyAudience?.testGroups,
        product.id,
      );

      return {
        ...acc,
        [product.id]: totalBoxCount,
      };
    }, {});
  }, [products, audience?.legacyAudience?.testGroups]);

  return {
    data,
    isLoading: isLoadingAudience || isLoadingProducts,
    isError: isErrorAudience || isErrorProducts,
  };
};

export default useGetBoxCountsPerProduct;
